import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Container, Typography, TextField, Button, Paper, Box, Link, Alert } from '@mui/material';
import { Formik, Form, Field, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import AuthService from '../services/auth.service';

interface ForgotPasswordFormValues {
  email: string;
}

const ForgotPasswordSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
});

const ForgotPasswordPage: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const initialValues: ForgotPasswordFormValues = {
    email: '',
  };

  const handleSubmit = async (
    values: ForgotPasswordFormValues,
    { setSubmitting }: FormikHelpers<ForgotPasswordFormValues>
  ) => {
    try {
      setError(null);
      await AuthService.requestPasswordReset({ email: values.email });
      setSuccess('If your email exists in our database, you will receive a password reset link shortly.');
    } catch (err: any) {
      // We don't show specific errors for security reasons
      setSuccess('If your email exists in our database, you will receive a password reset link shortly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Paper elevation={3} sx={{ p: 4, mt: 8 }}>
        <Typography variant="h4" component="h1" align="center" gutterBottom>
          Forgot Password
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
        
        {!success && (
          <Formik
            initialValues={initialValues}
            validationSchema={ForgotPasswordSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched, isSubmitting }) => (
              <Form>
                <Typography variant="body1" gutterBottom>
                  Enter your email address, and we'll send you a link to reset your password.
                </Typography>
                
                <Field
                  as={TextField}
                  fullWidth
                  margin="normal"
                  id="email"
                  name="email"
                  label="Email Address"
                  type="email"
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                />
                
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={isSubmitting}
                  sx={{ mt: 3, mb: 2 }}
                >
                  {isSubmitting ? 'Sending...' : 'Send Reset Link'}
                </Button>
                
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Link component={RouterLink} to="/login" variant="body2">
                    Back to Login
                  </Link>
                </Box>
              </Form>
            )}
          </Formik>
        )}
        
        {success && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Link component={RouterLink} to="/login" variant="body2">
              Back to Login
            </Link>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default ForgotPasswordPage;
